import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { OutputCard, CompliancePanel } from "@/components/OutputCard";
import { Button } from "@/components/ui/button";
import { FileText, MapPin, Megaphone, Sparkles, Trash2, Library, ChevronDown, Mail, Video, Newspaper, Film } from "lucide-react";

export default function ListingsView({ onMakeVideo }) {
  const [listings, setListings] = useState(null);
  const [open, setOpen] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/listings");
      setListings(data);
    } catch {
      setListings([]);
    }
  };
  useEffect(() => { load(); }, []);

  const del = async (id, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/listings/${id}`);
      setListings((l) => l.filter((x) => x.id !== id));
      toast.success("Listing deleted");
    } catch {
      toast.error("Couldn't delete");
    }
  };

  if (listings === null) return <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-16 bg-white border border-slate-200 rounded-lg animate-pulse" />)}</div>;

  if (listings.length === 0)
    return (
      <div className="bg-white border border-dashed border-slate-300 rounded-lg p-12 text-center" data-testid="listings-empty">
        <Library className="h-10 w-10 text-slate-300 mx-auto" />
        <p className="text-slate-500 mt-4">No saved listings yet. Generate one and it'll appear here automatically.</p>
      </div>
    );

  return (
    <div className="space-y-3" data-testid="listings-list">
      {listings.map((l) => (
        <div key={l.id} className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid={`listing-${l.id}`}>
          <div role="button" tabIndex={0} onClick={() => setOpen(open === l.id ? null : l.id)} className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50 transition-colors text-left cursor-pointer">
            <div className="min-w-0">
              <p className="font-medium truncate">{l.title}</p>
              <p className="text-xs text-slate-500 mt-0.5">{new Date(l.created_at).toLocaleString()}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0 ml-4">
              <button onClick={(e) => { e.stopPropagation(); onMakeVideo?.(l); }} data-testid={`make-video-${l.id}`}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium text-slate-600 border border-slate-200 hover:bg-slate-900 hover:text-white hover:border-slate-900 transition-colors">
                <Film className="h-3.5 w-3.5" /> Make video
              </button>
              <button onClick={(e) => del(l.id, e)} data-testid={`delete-${l.id}`} className="p-2 text-slate-400 hover:text-red-600 transition-colors"><Trash2 className="h-4 w-4" /></button>
              <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open === l.id ? "rotate-180" : ""}`} />
            </div>
          </div>
          {open === l.id && (
            <div className="p-5 border-t border-slate-100 space-y-4 bg-slate-50/40">
              <CompliancePanel compliance={l.outputs.compliance} />
              <OutputCard title="Listing Description" icon={FileText} testid={`lib-desc-${l.id}`} copyText={l.outputs.description}>{l.outputs.description}</OutputCard>
              <OutputCard title="Neighborhood Highlights" icon={MapPin} testid={`lib-nb-${l.id}`} copyText={l.outputs.neighborhood}>{l.outputs.neighborhood}</OutputCard>
              <OutputCard title="Social Captions" icon={Megaphone} testid={`lib-soc-${l.id}`} copyText={l.outputs.social}>{l.outputs.social}</OutputCard>
              <OutputCard title="Headlines & Hooks" icon={Sparkles} testid={`lib-head-${l.id}`} copyText={l.outputs.headlines}>{l.outputs.headlines}</OutputCard>
              {l.outputs.email && <OutputCard title='"Just Listed" Email' icon={Mail} testid={`lib-email-${l.id}`} copyText={l.outputs.email}>{l.outputs.email}</OutputCard>}
              {l.outputs.video && <OutputCard title="Video Walkthrough Script" icon={Video} testid={`lib-video-${l.id}`} copyText={l.outputs.video}>{l.outputs.video}</OutputCard>}
              {l.outputs.flyer && <OutputCard title="Printable Flyer Copy" icon={Newspaper} testid={`lib-flyer-${l.id}`} copyText={l.outputs.flyer} printable>{l.outputs.flyer}</OutputCard>}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
